import { useState, forwardRef, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import { InviteModal } from '@/components/InviteModal';
import { useInviteToken } from '@/hooks/useInviteToken';
import { calculateDaysRemaining } from '@/lib/utils';
import { toast } from 'sonner';

interface LeaderboardEntry {
  user_id: string;
  profile_user_id: string | null;
  beer_count: number;
  wine_count: number;
  shot_count: number;
  total: number;
  longest_streak: number;
}

interface LeaderboardSectionProps {
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

export const LeaderboardSection = forwardRef<HTMLElement, LeaderboardSectionProps>(
  function LeaderboardSection({ onModalOpen, onModalClose }, ref) {
    const { group, user } = useAuth();
    const { t } = useLanguage();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const { token: inviteToken } = useInviteToken();

    // Notify parent of modal state
    useEffect(() => {
      if (showInviteModal) {
        onModalOpen?.();
      } else {
        onModalClose?.();
      }
    }, [showInviteModal, onModalOpen, onModalClose]);

    const { data: leaderboard, isLoading } = useQuery({
      queryKey: ['leaderboard', group?.id],
      queryFn: async () => {
        if (!group) return [];

        // Get all memberships for this group
        const { data: memberships } = await supabase
          .from('memberships')
          .select('user_id')
          .eq('group_id', group.id);

        if (!memberships) return [];

        // Get profiles for these users using secure function (requires group membership)
        const { data: profiles } = await supabase
          .rpc('get_group_member_profiles', { target_group_id: group.id });

        // Get drink logs for these users
        const { data: drinkLogs } = await supabase
          .from('drink_logs')
          .select('user_id, drink_type, logged_at')
          .eq('group_id', group.id);

        // Helper to calculate longest streak for a user (never negative)
        const calculateLongestStreak = (userId: string): number => {
          if (!group) return 0;
          
          const start = new Date(group.challenge_start);
          start.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const userLogs = (drinkLogs || []).filter(log => log.user_id === userId);
          
          if (userLogs.length === 0) {
            return Math.max(0, Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
          }

          const sortedLogs = [...userLogs].sort((a, b) => 
            new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime()
          );

          let longestStreak = 0;
          
          const firstDrink = new Date(sortedLogs[0].logged_at);
          firstDrink.setHours(0, 0, 0, 0);
          longestStreak = Math.max(longestStreak, Math.floor((firstDrink.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));

          for (let i = 0; i < sortedLogs.length - 1; i++) {
            const current = new Date(sortedLogs[i].logged_at);
            current.setHours(0, 0, 0, 0);
            const next = new Date(sortedLogs[i + 1].logged_at);
            next.setHours(0, 0, 0, 0);
            const gap = Math.floor((next.getTime() - current.getTime()) / (1000 * 60 * 60 * 24)) - 1;
            longestStreak = Math.max(longestStreak, gap);
          }

          const lastDrink = new Date(sortedLogs[sortedLogs.length - 1].logged_at);
          lastDrink.setHours(0, 0, 0, 0);
          longestStreak = Math.max(longestStreak, Math.floor((today.getTime() - lastDrink.getTime()) / (1000 * 60 * 60 * 24)));

          return longestStreak;
        };

        // Build leaderboard
        const leaderboardMap = new Map<string, LeaderboardEntry>();

        // Initialize with all members
        for (const membership of memberships) {
          const profile = profiles?.find(p => p.id === membership.user_id);
          leaderboardMap.set(membership.user_id, {
            user_id: membership.user_id,
            profile_user_id: profile?.user_id || null,
            beer_count: 0,
            wine_count: 0,
            shot_count: 0,
            total: 0,
            longest_streak: calculateLongestStreak(membership.user_id),
          });
        }

        // Count drinks
        for (const log of drinkLogs || []) {
          const entry = leaderboardMap.get(log.user_id);
          if (entry) {
            if (log.drink_type === 'beer') entry.beer_count++;
            else if (log.drink_type === 'wine') entry.wine_count++;
            else if (log.drink_type === 'shot') entry.shot_count++;
            entry.total++;
          }
        }

        // Sort: ascending by total drinks, then descending by longest streak
        return Array.from(leaderboardMap.values()).sort((a, b) => {
          if (a.total !== b.total) return a.total - b.total;
          return b.longest_streak - a.longest_streak;
        });
      },
      enabled: !!group,
    });

    const daysRemaining = group ? calculateDaysRemaining(group.challenge_end) : 0;

    // Determine if we should show single-member tooltip
    const memberCount = leaderboard?.length || 0;
    const isSingleMember = memberCount === 1;
    
    // Check if challenge has started (challenge_start is in the past)
    const hasStarted = useMemo(() => {
      if (!group) return false;
      const now = new Date();
      const start = new Date(group.challenge_start);
      return now >= start;
    }, [group]);

    // Display code: use invite token
    const displayCode = inviteToken || '···';

    // Handle copying invite link
    const handleCopyInviteCode = () => {
      if (inviteToken) {
        const joinUrl = `https://tugedr.com/join?token=${inviteToken}`;
        navigator.clipboard.writeText(joinUrl);
        toast.success(t('inviteModalCopied'));
      }
    };

    return (
      <section ref={ref} id="leaderboard" className="py-8 border-t border-border">
        {/* Section Header */}
        <div className="px-6 pb-4 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-serif font-bold mb-2">{t('leaderboardTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {daysRemaining > 0 ? `${daysRemaining} ${t('leaderboardDaysRemaining')}` : t('leaderboardComplete')}
          </p>
          {group && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('leaderboardInviteCode')}{' '}
              <button
                onClick={() => inviteToken && setShowInviteModal(true)}
                className="font-mono underline underline-offset-2 decoration-primary/50 hover:decoration-primary transition-colors"
              >
                {displayCode}
              </button>
            </p>
          )}
        </div>

        {/* Invite Modal */}
        {showInviteModal && inviteToken && (
          <InviteModal 
            token={inviteToken} 
            onClose={() => setShowInviteModal(false)} 
          />
        )}

        {/* Single-member tooltip */}
        {isSingleMember && group && (
          <div className="px-6 max-w-lg mx-auto mb-4">
            <div className="relative">
              {/* Arrow pointing up */}
              <div 
                className="absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0"
                style={{
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: '10px solid hsl(var(--primary))',
                }}
              />
              {/* Tooltip body */}
              <div className="bg-primary text-primary-foreground px-4 py-3 rounded-lg text-center shadow-lg">
                {!hasStarted ? (
                  // State A: Before midnight (challenge not started)
                  <div className="space-y-1">
                    <p className="text-base font-semibold">🕛 {t('leaderboardTooltipHeadline')}</p>
                    <p className="text-sm">
                      {t('leaderboardTooltipBeforeMidnightBody')}{' '}
                      <button
                        onClick={() => inviteToken && setShowInviteModal(true)}
                        className="font-mono font-bold underline underline-offset-2 decoration-primary-foreground/50 hover:decoration-primary-foreground transition-colors"
                      >
                        {displayCode}
                      </button>
                      ?
                    </p>
                  </div>
                ) : (
                  // State B: After midnight, still only one member
                  <div className="space-y-1">
                    <p className="text-base font-semibold">{t('leaderboardTooltipAfterHeadline')}</p>
                    <p className="text-sm">
                      {t('leaderboardTooltipAfterMidnightBody')}{' '}
                      <button
                        onClick={() => inviteToken && setShowInviteModal(true)}
                        className="font-mono font-bold underline underline-offset-2 decoration-primary-foreground/50 hover:decoration-primary-foreground transition-colors"
                      >
                        {displayCode}
                      </button>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Table */}
        <div className="px-6 max-w-lg mx-auto">
          {isLoading ? (
            <div className="text-center text-muted-foreground">{t('loading')}</div>
          ) : leaderboard && leaderboard.length > 0 ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('leaderboardRankings')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-0.5">
                  {/* Header row */}
                  <div className="grid grid-cols-[minmax(0,1fr)_36px_28px_24px_24px_24px] gap-1.5 text-xs text-muted-foreground font-medium pb-1.5 border-b border-border/60 px-1">
                    <div className="flex items-center gap-2">
                      <span>{t('leaderboardUser')}</span>
                      <span className="text-center font-bold">{t('leaderboardTotal')}</span>
                    </div>
                    <span className="text-center">🗓️</span>
                    <span className="text-center">🍺</span>
                    <span className="text-center">🍷</span>
                    <span className="text-center">🥃</span>
                    <span></span>
                  </div>

                  {/* Data rows */}
                  {leaderboard.map((entry) => {
                    // Competition ranking: find first index with same total and streak
                    const rank = leaderboard.findIndex(
                      e => e.total === entry.total && e.longest_streak === entry.longest_streak
                    ) + 1;
                    
                    const displayName = entry.profile_user_id || 'Anonymous';
                    
                    return (
                      <div 
                        key={entry.user_id}
                        className={`grid grid-cols-[minmax(0,1fr)_36px_28px_24px_24px_24px] gap-1.5 py-2 rounded-md px-1 ${
                          entry.user_id === user?.id ? 'bg-primary/5' : ''
                        }`}
                      >
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="text-xs sm:text-sm font-medium w-5 shrink-0 tabular-nums">
                            {rank}.
                          </span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span 
                                className="text-xs sm:text-sm font-medium truncate"
                              >
                                {displayName}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[200px]">
                              <p className="break-words">{displayName}</p>
                            </TooltipContent>
                          </Tooltip>
                          {entry.user_id === user?.id && (
                            <span className="text-[10px] text-muted-foreground shrink-0 ml-0.5">{t('leaderboardYou')}</span>
                          )}
                          <span className="text-xs sm:text-sm font-bold tabular-nums ml-auto">{entry.total}</span>
                        </div>
                        <span className="text-center text-xs sm:text-sm tabular-nums">{entry.longest_streak}</span>
                        <span className="text-center text-xs sm:text-sm tabular-nums">{entry.beer_count}</span>
                        <span className="text-center text-xs sm:text-sm tabular-nums">{entry.wine_count}</span>
                        <span className="text-center text-xs sm:text-sm tabular-nums">{entry.shot_count}</span>
                        <span></span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center text-muted-foreground">
              {t('leaderboardNoParticipants')}
            </div>
          )}
        </div>
      </section>
    );
  }
);
